import { useRef, useState } from 'react'
import { api } from '../lib/api'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'

interface RecoveryEmailVerificationPanelProps {
  token: string
  userId: number
  onClosed: () => void
}

function messageFor(error: unknown, fallback: string): string {
  return userMessageForError(error, fallback)
}

/**
 * 복구 이메일 인증 링크 전용 화면입니다.
 *
 * 토큰은 사용자가 버튼을 누를 때 중앙 confirm/cancel API로 한 번 전달할 뿐이며,
 * 로그나 브라우저 저장소에 남기지 않습니다. 자동 confirm을 피한 이유는 개발 모드의
 * React StrictMode가 effect를 두 번 실행해 일회용 토큰을 중복 사용할 수 있기 때문입니다.
 */
export function RecoveryEmailVerificationPanel({
  token,
  userId,
  onClosed,
}: RecoveryEmailVerificationPanelProps) {
  const { t } = useI18n()
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(
    token
      ? t('recoveryVerify.message.ready')
      : t('recoveryVerify.message.tokenMissing'),
  )
  const completed = useRef(false)

  async function confirm() {
    if (!token || completed.current) return
    setSubmitting(true)
    try {
      await api.confirmRecoveryEmailVerification(token)
      completed.current = true
      setMessage(t('recoveryVerify.message.completed'))
      history.replaceState({}, '', '/')
    } catch (error) {
      setMessage(messageFor(error, t('recoveryVerify.error.generic')))
    } finally {
      setSubmitting(false)
    }
  }

  async function cancel() {
    if (!token || userId <= 0 || completed.current) {
      setMessage(t('recoveryVerify.message.cancelInfoMissing'))
      return
    }
    setSubmitting(true)
    try {
      await api.cancelRecoveryEmailVerification(userId, token)
      completed.current = true
      setMessage(t('recoveryVerify.message.cancelled'))
      history.replaceState({}, '', '/')
    } catch (error) {
      setMessage(messageFor(error, t('recoveryVerify.error.generic')))
    } finally {
      setSubmitting(false)
    }
  }

  function close() {
    history.replaceState({}, '', '/')
    onClosed()
  }

  return (
    <section className="card auth-card recovery-card">
      <p className="eyebrow">{t('recoveryVerify.eyebrow')}</p>
      <h2>{t('recoveryVerify.title')}</h2>
      <p>{t('recoveryVerify.description')}</p>
      <div className="hero-actions">
        <button className="button primary" type="button" disabled={!token || submitting || completed.current} onClick={() => void confirm()}>
          {submitting ? t('common.processing') : t('recoveryVerify.action.confirm')}
        </button>
        <button className="button danger" type="button" disabled={!token || userId <= 0 || submitting || completed.current} onClick={() => void cancel()}>
          {t('recoveryVerify.action.notRequested')}
        </button>
      </div>
      <p className="notice" role="status">{message}</p>
      <button className="button secondary" type="button" onClick={close}>{t('recoveryVerify.action.goToLogin')}</button>
    </section>
  )
}
